"use client";

import { CtaButton } from "@/base/component/cta-button";
import { Card, CardBody, CardFooter, CardHeader } from "@heroui/card";
import { Link } from "@heroui/react";
import { IconBrandGoogle, IconChartBar } from "@tabler/icons-react";
import { useRouter } from "next/navigation";

export default function HowItWorksPage() {
  const router = useRouter();
  return (
    <div className="py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
            How it works
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg">
            Our advanced seo platform analyzes your website and provides
            data-driven recommendations to improve your content and technical
            optimization
          </p>
        </div>

        <div className="mt-16">
          <h2 className="text-2xl font-bold tracking-tight mb-8">
            Analysis steps
          </h2>
          <div className="grid gap-8 md:grid-cols-4">
            <Card className="relative rounded-2xl p-6">
              <CardBody className="flex flex-col gap-2 items-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary bg-opacity-10">
                  <span className="text-xl font-semibold text-primary">1</span>
                </div>
                <h3 className="text-lg font-medium">Structure analysis</h3>
              </CardBody>
              <CardFooter>
                <p>
                  Deep scan of your website architecture, navigation paths, and
                  content hierarchy to identify structural improvements
                </p>
              </CardFooter>
            </Card>

            <Card className="relative rounded-2xl p-6">
              <CardBody className="flex flex-col gap-2 items-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary bg-opacity-10">
                  <span className="text-xl font-semibold text-primary">2</span>
                </div>
                <h3 className="text-lg font-medium">Data collection</h3>
              </CardBody>
              <CardFooter className="text-center">
                <p>
                  Gathering website data through{" "}
                  <Link
                    href="https://dataforseo.com/"
                    target="_blank"
                    className="inline-flex items-center gap-1 text-primary hover:text-primary-600 font-medium"
                  >
                    <IconChartBar className="w-4 h-4" />
                    DataForSEO
                  </Link>{" "}
                  api for comprehensive analysis
                </p>
              </CardFooter>
            </Card>

            <Card className="relative rounded-2xl p-6">
              <CardBody className="flex flex-col gap-2 items-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary bg-opacity-10">
                  <span className="text-xl font-semibold text-primary">3</span>
                </div>
                <h3 className="text-lg font-medium">Ai processing</h3>
              </CardBody>
              <CardFooter>
                <p>
                  Advanced ai models process your data using machine learning
                  algorithms to identify optimization opportunities
                </p>
              </CardFooter>
            </Card>

            <Card className="relative rounded-2xl p-6">
              <CardBody className="flex flex-col gap-2 items-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary bg-opacity-10">
                  <span className="text-xl font-semibold text-primary">4</span>
                </div>
                <h3 className="text-lg font-medium">Results</h3>
              </CardBody>
              <CardFooter>
                <p>
                  Actionable recommendations and insights to improve your search
                  rankings, traffic, and online visibility
                </p>
              </CardFooter>
            </Card>
          </div>
        </div>

        <div className="mt-16">
          <h2 className="text-2xl font-bold tracking-tight">Data insights</h2>
          <p className="mt-4 text-lg">
            Our platform analyzes crucial website metrics and seo factors to
            provide comprehensive optimization recommendations
          </p>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <Card className="rounded-2xl p-6">
              <CardHeader className="flex flex-col gap-0 items-start">
                <h3 className="text-lg font-medium">Website data</h3>
                <p className="mt-2">
                  Technical analysis powered by{" "}
                  <Link
                    href="https://pagespeed.web.dev/"
                    target="_blank"
                    className="inline-flex items-center gap-1 text-primary hover:text-primary-600 font-medium"
                  >
                    <IconBrandGoogle className="w-4 h-4" />
                    PageSpeed Insights
                  </Link>
                </p>
              </CardHeader>
              <CardBody>
                <ul className="mt-4 space-y-3">
                  {[
                    "Complete website crawl and sitemap analysis",
                    "Content quality and keyword optimization check",
                    "Technical seo and meta tags evaluation",
                    "Core web vitals and loading speed metrics",
                  ].map((item) => (
                    <li key={item} className="flex items-center">
                      <svg
                        className="mr-2 h-5 w-5 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>

            <Card className="rounded-2xl p-6">
              <CardHeader className="flex flex-col gap-0 items-start">
                <h3 className="text-lg font-medium">Seo metrics</h3>
                <p className="mt-2">
                  Comprehensive analysis through{" "}
                  <Link
                    href="https://dataforseo.com/"
                    target="_blank"
                    className="inline-flex items-center gap-1 text-primary hover:text-primary-600 font-medium"
                  >
                    <IconChartBar className="w-4 h-4" />
                    DataForSEO
                  </Link>
                </p>
              </CardHeader>
              <CardBody>
                <ul className="space-y-3">
                  {[
                    "Content structure",
                    "Technical optimization",
                    "Page performance",
                    "Seo data analysis",
                  ].map((item) => (
                    <li key={item} className="flex items-center">
                      <svg
                        className="mr-2 h-5 w-5 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </div>
        </div>

        <Card className="mt-16 p-4">
          <CardBody>
            <h2 className="text-2xl font-bold tracking-tight">
              Processing workflow
            </h2>
            <div className="mt-6 prose prose-blue max-w-none">
              <p className="">
                The system processes website data through multiple stages to
                generate recommendations
              </p>
              <div className="mt-8 space-y-6">
                {[
                  {
                    title: "Initial scan",
                    description:
                      "Our system performs a thorough crawl of your website, analyzing page structure, internal linking, content quality, and technical seo elements to build a complete picture of your site's current state",
                  },
                  {
                    title: "Data gathering",
                    description:
                      "We collect website data and seo metrics through dataforseo api to analyze optimization opportunities",
                  },
                  {
                    title: "Ai analysis",
                    description:
                      "Multiple specialized ai models process the collected data, identifying patterns and potential issues that impact your website performance",
                  },
                  {
                    title: "Output generation",
                    description:
                      "Based on the analyzed data, we generate prioritized, actionable recommendations to improve your content quality and technical optimization",
                  },
                ].map((step, index) => (
                  <div key={step.title} className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary bg-opacity-10 text-primary">
                        {index + 1}
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium">{step.title}</h3>
                      <p className="mt-2">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="w-full flex justify-center mt-12">
        <CtaButton
          radius="full"
          size="lg"
          variant="solid"
          color="primary"
          onPress={() => router.push("/#auth")}
        >
          Start SEO analysis
        </CtaButton>
      </div>
    </div>
  );
}
